import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Logo, StatusDot, useSystemTheme } from "@acs/ui";
import { LanguageSelect, useI18n } from "../../i18n/index.js";
import {
  FAQ,
  FEATURES,
  HOW_STEPS,
  INDUSTRY_KEYS,
  MARKETS,
  NAV_LINKS,
  OBJECTIONS,
  BILLING_ITEMS,
  INCLUDED_MINUTES,
  POLISH_PACKAGES,
  SAMPLE_CALL,
} from "./content.js";
import { useReveal } from "./useReveal.js";

/**
 * Public marketing page at `/landing`; `/` remains the portal's sign-in route.
 * Copy and feature claims are reviewed in docs/landing-copy-audit.md.
 * Examples are labelled illustrations, and all five UI languages share the
 * same offer scope. Language selection sits in the header bar; the theme
 * follows the device's light/dark (no control, and the signed-in owner's own
 * choice does not apply here).
 */
export function LandingPage() {
  const { t, tag } = useI18n();
  useSystemTheme();
  const billingValues = {
    minutes: INCLUDED_MINUTES,
    packages: POLISH_PACKAGES.map((minutes) => new Intl.NumberFormat(tag).format(minutes)).join(' / '),
  };
  const rootRef = useReveal<HTMLDivElement>();
  // First question open: the FAQ's job here is to be read, and a wall of
  // collapsed rows reads as a list of links. -1 means "all closed".
  const [openFaq, setOpenFaq] = useState(0);
  const [showSample, setShowSample] = useState(false);

  return (
    <div className="lp" ref={rootRef}>
      <a className="lp-skip" href="#main">
        {t("landing.skipToMain")}
      </a>

      <header className="lp-header">
        <nav className="lp-nav" aria-label={t("nav.navigation")}>
          {/* Back to the top of THIS page, not to `/` — `/` is the sign-in
              door, and a logo that signs you out of the marketing page is not
              what anyone means by clicking a logo. */}
          <Link
            to="/landing"
            className="lp-brand"
            aria-label={t("account.home")}
          >
            <Logo />
          </Link>
          <div className="lp-nav__links">
            {NAV_LINKS.map((link) => (
              <a key={link.id} href={`#${link.id}`} className="lp-nav__link">
                {t(link.labelKey)}
              </a>
            ))}
          </div>
          {/* Sign-in and the CTA both land on /signin — Google is the only
              provider, so there is no separate sign-up to send anyone to. They
              are kept apart anyway: a returning owner scans for "Sign in" and
              should not have to read a marketing verb to find their dashboard. */}
          {/* In the bar, not the footer: the language is chosen before anything
              else is read, and it is the language a new account starts in. */}
          <LanguageSelect className="lp-nav__lang" />
          <Link to="/signin" className="lp-signin">
            {t("landing.signIn")}
          </Link>
          <Link
            to="/signin"
            className="tc-btn tc-btn--primary tc-btn--md lp-nav__cta"
          >
            {t("landing.cta")}
          </Link>
        </nav>
      </header>

      <main id="main">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="lp-hero">
          <div data-reveal>
            <span className="lp-eyebrow-pill">
              <StatusDot tone="success" pulse />
              {t("landing.hero.eyebrow")}
            </span>
            <h1 className="lp-h1">{t("landing.hero.title")}</h1>
            <p className="lp-lede">{t("landing.hero.sub")}</p>
            <div className="lp-cta-row">
              <Link to="/signin" className="tc-btn tc-btn--primary tc-btn--lg">
                {t("landing.cta")}
                <Icon name="arrow-right" size={18} strokeWidth={2.5} />
              </Link>
              <button
                type="button"
                className="tc-btn tc-btn--secondary tc-btn--lg"
                aria-expanded={showSample}
                aria-controls="lp-sample"
                onClick={() => setShowSample((open) => !open)}
              >
                <Icon name={showSample ? "chevron-up" : "play"} size={16} />
                {showSample
                  ? t("landing.hero.hideSample")
                  : t("landing.hero.secondaryCta")}
              </button>
            </div>
            <ul className="lp-trust-row">
              <li>
                <Icon name="check" size={16} strokeWidth={2.5} />
                {t("landing.hero.trust.gdpr")}
              </li>
              <li>
                <Icon name="check" size={16} strokeWidth={2.5} />
                {t("landing.hero.trust.number")}
              </li>
              <li>
                <Icon name="check" size={16} strokeWidth={2.5} />
                {t("landing.hero.trust.noRecording")}
              </li>
            </ul>
          </div>

          <div className="lp-hero__visual" data-reveal>
            {/* A mock of the dashboard, not a screenshot: the appointments are
                sample data and the caption says so, so nobody reads the two
                rows as a claim about anyone's real calendar. */}
            <figure className="lp-window">
              <figcaption className="lp-window__bar">
                <span className="lp-window__dot lp-window__dot--r" />
                <span className="lp-window__dot lp-window__dot--y" />
                <span className="lp-window__dot lp-window__dot--g" />
                <span className="lp-window__host">topcall24.com</span>
                <span className="lp-window__caption">
                  {t("landing.hero.preview.caption")}
                </span>
              </figcaption>
              <div className="lp-window__body">
                <div className="lp-window__head">
                  <span className="lp-window__title">
                    {t("landing.hero.preview.title")}
                  </span>
                  <span className="lp-window__badge">
                    <StatusDot tone="success" />
                    {t("landing.hero.preview.badge")}
                  </span>
                </div>
                <div className="lp-appt">
                  <span className="lp-appt__time">09:30</span>
                  <span className="lp-appt__what">
                    <span className="lp-appt__service">
                      {t("landing.hero.preview.a1Service")}
                    </span>
                    <span className="lp-appt__who">
                      {t("landing.hero.preview.a1Who")}
                    </span>
                  </span>
                  <StatusDot tone="success" />
                </div>
                <div className="lp-appt">
                  <span className="lp-appt__time">11:00</span>
                  <span className="lp-appt__what">
                    <span className="lp-appt__service">
                      {t("landing.hero.preview.a2Service")}
                    </span>
                    <span className="lp-appt__who">
                      {t("landing.hero.preview.a2Who")}
                    </span>
                  </span>
                  <StatusDot tone="success" />
                </div>
              </div>
            </figure>

            <div className="lp-samplecard" id="lp-sample" hidden={!showSample}>
              <div className="lp-samplecard__label">
                <Icon name="phone-incoming" size={16} />
                {t("landing.hero.sample.label")}
              </div>
              <div className="lp-samplecard__turns">
                {SAMPLE_CALL.slice(0, 3).map((turn) => (
                  <p
                    key={turn.key}
                    className={`lp-bubble lp-bubble--${turn.speaker}`}
                  >
                    {t(turn.key)}
                  </p>
                ))}
              </div>
              <a href="#call" className="lp-samplecard__more">
                {t("landing.call.title")}
                <Icon name="arrow-right" size={14} strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </section>

        {/* ── Who it is for ────────────────────────────────────────────── */}
        <section
          className="lp-industries"
          aria-labelledby="lp-industries-h"
          data-reveal
        >
          <h2 className="lp-industries__title" id="lp-industries-h">
            {t("landing.industries.title")}
          </h2>
          <ul className="lp-industries__list">
            {INDUSTRY_KEYS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </section>

        {/* ── How it works ─────────────────────────────────────────────── */}
        <section id="how" className="lp-section" aria-labelledby="lp-how-h">
          <div
            className="lp-section__head lp-section__head--center"
            data-reveal
          >
            <p className="lp-eyebrow">{t("landing.how.eyebrow")}</p>
            <h2 className="lp-h2" id="lp-how-h">
              {t("landing.how.title")}
            </h2>
          </div>
          <ol className="lp-steps">
            {HOW_STEPS.map((step, i) => (
              <li key={step.titleKey} className="lp-step" data-reveal>
                <span className="lp-step__icon">
                  <Icon name={step.icon} size={26} />
                </span>
                <span className="lp-step__n">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="lp-h3">{t(step.titleKey)}</h3>
                <p className="lp-body">{t(step.bodyKey)}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Features ─────────────────────────────────────────────────── */}
        <section
          id="features"
          className="lp-band"
          aria-labelledby="lp-features-h"
        >
          <div className="lp-section">
            <div className="lp-section__head" data-reveal>
              <p className="lp-eyebrow">{t("landing.features.eyebrow")}</p>
              <h2 className="lp-h2" id="lp-features-h">
                {t("landing.features.title")}
              </h2>
            </div>
            <ul className="lp-grid3">
              {FEATURES.map((feature) => (
                <li key={feature.titleKey} className="lp-card" data-reveal>
                  <span className="lp-card__icon">
                    <Icon name={feature.icon} size={22} />
                  </span>
                  <h3 className="lp-h4">{t(feature.titleKey)}</h3>
                  <p className="lp-body lp-body--sm">{t(feature.bodyKey)}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── The example call ─────────────────────────────────────────────
            This is the page's proof section. It replaces the template's
            invented statistics and testimonials: rather than assert that the
            assistant is good, show the script it runs — including the AI
            disclosure in its first line, which is the thing owners most want
            to see before they point a real phone line at it. */}
        <section id="call" className="lp-section" aria-labelledby="lp-call-h">
          <div
            className="lp-section__head lp-section__head--center"
            data-reveal
          >
            <p className="lp-eyebrow">{t("landing.call.eyebrow")}</p>
            <h2 className="lp-h2" id="lp-call-h">
              {t("landing.call.title")}
            </h2>
          </div>
          <div className="lp-transcript" data-reveal>
            {SAMPLE_CALL.map((turn) => (
              <p
                key={turn.key}
                className={`lp-bubble lp-bubble--${turn.speaker}`}
              >
                {t(turn.key)}
              </p>
            ))}
          </div>
          <p className="lp-note" data-reveal>
            <Icon name="info" size={16} />
            {t("landing.call.note")}
          </p>
        </section>

        {/* ── Pricing ──────────────────────────────────────────────────── */}
        <section
          id="pricing"
          className="lp-band"
          aria-labelledby="lp-pricing-h"
        >
          <div className="lp-section">
            <div
              className="lp-section__head lp-section__head--center"
              data-reveal
            >
              <p className="lp-eyebrow">{t("landing.pricing.eyebrow")}</p>
              <h2 className="lp-h2" id="lp-pricing-h">
                {t("landing.pricing.title")}
              </h2>
              <p className="lp-lede lp-lede--center">
                {t("landing.pricing.sub")}
              </p>
            </div>
            <ul className="lp-grid3">
              {BILLING_ITEMS.map((plan) => (
                <li
                  key={plan.nameKey}
                  className={`lp-plan${plan.featured ? " lp-plan--featured" : ""}`}
                  data-reveal
                >
                  {plan.featured && (
                    <span className="lp-plan__ribbon">
                      {t("landing.pricing.offer")}
                    </span>
                  )}
                  <h3 className="lp-plan__name">{t(plan.nameKey)}</h3>
                  <p className="lp-plan__price">
                    {t(plan.valueKey, billingValues)}
                  </p>
                  <p className="lp-plan__detail">{t(plan.detailKey)}</p>
                  <p className="lp-body lp-body--sm">{t(plan.descKey, billingValues)}</p>
                  <Link
                    to="/signin"
                    className={`tc-btn tc-btn--${plan.featured ? "primary" : "secondary"} tc-btn--md tc-btn--block`}
                  >
                    {t("landing.cta")}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="lp-note lp-note--center" data-reveal>
              {t("landing.pricing.foot")}
            </p>
          </div>
        </section>

        {/* ── Objections ───────────────────────────────────────────────── */}
        <section className="lp-section" aria-labelledby="lp-trust-h">
          <div
            className="lp-section__head lp-section__head--center"
            data-reveal
          >
            <h2 className="lp-h2" id="lp-trust-h">
              {t("landing.trust.title")}
            </h2>
          </div>
          <ul className="lp-grid2">
            {OBJECTIONS.map((item) => (
              <li key={item.titleKey} className="lp-objection" data-reveal>
                <span className="lp-objection__icon">
                  <Icon name={item.icon} size={20} />
                </span>
                <div>
                  <h3 className="lp-h4">{t(item.titleKey)}</h3>
                  <p className="lp-body lp-body--sm">{t(item.bodyKey)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section
          id="faq"
          className="lp-section lp-section--narrow"
          aria-labelledby="lp-faq-h"
        >
          <div
            className="lp-section__head lp-section__head--center"
            data-reveal
          >
            <h2 className="lp-h2" id="lp-faq-h">
              {t("landing.faq.title")}
            </h2>
          </div>
          <div className="lp-faq" data-reveal>
            {FAQ.map((entry, i) => {
              const open = openFaq === i;
              return (
                <div key={entry.questionKey} className="lp-faq__item">
                  <h3>
                    <button
                      type="button"
                      className="lp-faq__q"
                      aria-expanded={open}
                      aria-controls={`lp-faq-a${i}`}
                      id={`lp-faq-q${i}`}
                      onClick={() => setOpenFaq(open ? -1 : i)}
                    >
                      <span>{t(entry.questionKey)}</span>
                      <Icon name={open ? "minus" : "plus"} size={18} />
                    </button>
                  </h3>
                  <div
                    className="lp-faq__a"
                    id={`lp-faq-a${i}`}
                    role="region"
                    aria-labelledby={`lp-faq-q${i}`}
                    hidden={!open}
                  >
                    {t(entry.answerKey, billingValues)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────────────────── */}
        <section className="lp-final" aria-labelledby="lp-final-h">
          <div data-reveal>
            <h2 className="lp-final__title" id="lp-final-h">
              {t("landing.final.title")}
            </h2>
            <p className="lp-final__sub">{t("landing.final.sub")}</p>
            <Link to="/signin" className="tc-btn tc-btn--primary tc-btn--lg">
              {t("landing.cta")}
              <Icon name="arrow-right" size={18} strokeWidth={2.5} />
            </Link>
            <p className="lp-final__foot">{t("landing.final.foot")}</p>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-footer__cols">
          <div className="lp-footer__brand">
            <Logo />
            <p className="lp-body lp-body--sm">{t("landing.footer.tagline")}</p>
            <p className="lp-footer__markets">
              {MARKETS.map((m) => (
                <span key={m.name}>
                  {m.flag} {m.name}
                </span>
              ))}
            </p>
          </div>
          <nav
            className="lp-footer__col"
            aria-label={t("landing.footer.product")}
          >
            <h2 className="lp-footer__h">{t("landing.footer.product")}</h2>
            {NAV_LINKS.map((link) => (
              <a key={link.id} href={`#${link.id}`}>
                {t(link.labelKey)}
              </a>
            ))}
          </nav>
          <nav
            className="lp-footer__col"
            aria-label={t("landing.footer.company")}
          >
            <h2 className="lp-footer__h">{t("landing.footer.company")}</h2>
            <a href="/about">{t("landing.footer.about")}</a>
            <a href="/contact">{t("landing.footer.contact")}</a>
          </nav>
          <nav
            className="lp-footer__col"
            aria-label={t("landing.footer.legal")}
          >
            <h2 className="lp-footer__h">{t("landing.footer.legal")}</h2>
            {/* Same three documents the sign-in page links; they are served
                outside the SPA and must exist before this page is published. */}
            <a href="/privacy">{t("landing.footer.privacy")}</a>
            <a href="/terms">{t("landing.footer.terms")}</a>
            <a href="/impressum">{t("landing.footer.impressum")}</a>
          </nav>
        </div>

        <div className="lp-footer__bottom">
          <span>
            © {new Date().getFullYear()} TopCall24 ·{" "}
            {t("landing.footer.rights")}
          </span>
          <span>{t("landing.footer.dataEu")}</span>
        </div>
      </footer>
    </div>
  );
}
